/**
 * EA-4288 — franchise referral attribution.
 *
 * A franchise store hands out links like `/products/foo?store=123`. Any storefront
 * URL carrying a well-formed `?store=` credits that store on the order being shopped:
 * the number rides on the cart as `referralStoreNumber`, Shopify copies cart
 * attributes onto the order's `note_attributes`, and the order-sync hook in Celigo
 * (script 6a3d7d6f48c8b4a733f4c6f6) forwards it to EA as `referralStoreNumber`. EA
 * owns the rebate rules; nothing here decides who gets paid.
 *
 * ⚠️ ATTRIBUTION ONLY. The referral store has no relationship to the store that
 * FULFILS the order. A shopper referred by 123 may be served by 789 and 123 still
 * gets the credit. So this module never reads or writes the store selection, never
 * calls availability or a store API, and never touches `store_id` / `store_number` /
 * `store_details` / `order_type`. It writes one cart attribute of its own. It applies
 * to every order type — delivery, pickup, shipment, catering — because it never looks.
 *
 * ⚠️ ONE ORDER, ONE BROWSER SESSION. A referral credits the order being shopped in
 * the browser session the referral link was followed in — no further:
 *
 *   • The browser session is marked by a SESSION cookie (no expiry; the browser drops
 *     it when it quits). It names the referred store and the cart it was stamped on.
 *   • A cart resumed in a LATER session has no marker, so its old referral is stale
 *     and is removed from the cart on the next page load.
 *   • A NEW cart (the one a placed order leaves behind) does not match the marker's
 *     cart, so it is never stamped; the next order starts clean unless the shopper
 *     follows another referral link.
 *
 * The cart attribute is still the only thing that reaches the order; the marker only
 * decides whether the cart's value is still current. No localStorage, no expiry clock.
 *
 * ⚠️ NON-CRITICAL. Every path here fails silent. A refused cart write, a blocked
 * cookie or a malformed URL can cost the referral — never the sale. Nothing in the
 * commerce flow awaits it.
 *
 * ── How it runs ────────────────────────────────────────────────────────────────
 * Once per page load (snippets/referral-attribution.liquid), before ea-datalayer.js:
 *
 *   1. read the incoming `?store=` value, if any, and sanitize it — a malformed value
 *      is the same as no value, so it can never displace a good referral
 *   2. work out the CURRENT referral: the session marker's store, but only when the
 *      marker names this browser's cart. The cart's identity comes from Shopify's own
 *      `cart` cookie (Liquid's `cart.token` renders empty — measured live), and its
 *      referral from Liquid (`window.__eaCartReferral`), so the steady state costs nothing
 *   3. resolve incoming against current (last touch), then make the cart match —
 *      writing the attribute, or clearing a stale one — and keep the marker in step.
 *      With no cart yet, the write is what creates it (measured live: an
 *      attributes-only /cart/update.js mints a cart and its cookie).
 *
 * If the cart write fails it is retried on the theme's next `cart:update` (typically
 * the first add-to-cart). Once the cart is confirmed it stops listening, so a referral
 * is never carried onto a new cart a checkout leaves behind.
 */

/* ──────────────────────────────────────────────────────────────────────────────
   CONFIGURATION — the one place referral behaviour is decided.
   ────────────────────────────────────────────────────────────────────────────── */
export const REFERRAL_ATTRIBUTION_CONFIG = Object.freeze({
  /** false turns the whole feature off: nothing is read, written or cleared. */
  enabled: true,

  /** The URL parameter a referral link carries, on any storefront URL: `?store=123`. */
  queryParam: 'store',

  attribution: Object.freeze({
    /**
     * 'last'  — a later referral link replaces the current referral.
     * 'first' — the current referral is kept.
     */
    model: 'last',
  }),

  /**
   * Format only — store numbers are numeric strings (`722`, `904`), kept as strings so
   * leading zeros survive. Whether the store exists or is open is deliberately NOT
   * checked here; EA reconciles that downstream.
   */
  validation: Object.freeze({
    pattern: /^\d+$/,
    maxLength: 10,
  }),

  /** The cart attribute the order sync reads. Mirrored in snippets/referral-attribution.liquid. */
  cartAttribute: 'referralStoreNumber',

  /** The browser-session marker: a cookie with no expiry, `<store>|<cart id>`. */
  sessionCookie: 'ea_referral_session',

  /** Shopify's own cart cookie — read only, for the cart's identity. */
  shopifyCartCookie: 'cart',
});

/** Fired on `document` when a referral link sets (or replaces) the referral. */
export const REFERRAL_CAPTURED_EVENT = 'ea:referral-store-captured';

const LOG_PREFIX = '[referral-attribution]';
const CART_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

/* ──────────────────────────────────────────────────────────────────────────────
   PURE RULES — no DOM, no network. Everything below is tested.
   ────────────────────────────────────────────────────────────────────────────── */

/**
 * A store number, or null when the value is not one. Trims, never casts: '0123'
 * stays '0123'.
 *
 * @param {unknown} raw
 * @param {typeof REFERRAL_ATTRIBUTION_CONFIG} [config]
 * @returns {string|null}
 */
export function sanitizeReferral(raw, config = REFERRAL_ATTRIBUTION_CONFIG) {
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const value = String(raw).trim();
  if (!value || value.length > config.validation.maxLength) return null;
  return config.validation.pattern.test(value) ? value : null;
}

/**
 * The sanitized referral on this URL, or null.
 *
 * @param {string} search `location.search`
 * @param {typeof REFERRAL_ATTRIBUTION_CONFIG} [config]
 * @returns {string|null}
 */
export function readIncomingReferral(search, config = REFERRAL_ATTRIBUTION_CONFIG) {
  try {
    return sanitizeReferral(new URLSearchParams(search || '').get(config.queryParam), config);
  } catch (_) {
    return null;
  }
}

/**
 * The cart's identity from its token (the `cart` cookie, or a /cart.js `token`).
 * Shopify tokens carry a secret after `?key=`; only the part before it identifies the
 * cart, and only that part is ever stored.
 *
 * @param {unknown} token raw or URI-encoded
 * @returns {string|null}
 */
export function cartIdFromToken(token) {
  if (typeof token !== 'string') return null;
  let raw = token;
  try {
    raw = decodeURIComponent(token);
  } catch (_) {
    /* use it as it is */
  }
  const id = raw.split('?')[0].trim();
  return CART_ID_PATTERN.test(id) ? id : null;
}

/** @typedef {{storeNumber: string, cartId: string}} SessionMarker */

/**
 * @param {unknown} raw the cookie value
 * @param {typeof REFERRAL_ATTRIBUTION_CONFIG} [config]
 * @returns {SessionMarker|null}
 */
export function parseSessionMarker(raw, config = REFERRAL_ATTRIBUTION_CONFIG) {
  if (typeof raw !== 'string' || !raw) return null;
  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch (_) {
    return null;
  }
  const [store, cart, ...rest] = value.split('|');
  if (rest.length) return null;
  const storeNumber = sanitizeReferral(store, config);
  const cartId = CART_ID_PATTERN.test(cart || '') ? cart : null;
  return storeNumber && cartId ? { storeNumber, cartId } : null;
}

/** @param {SessionMarker} marker */
export function formatSessionMarker(marker) {
  return encodeURIComponent(`${marker.storeNumber}|${marker.cartId}`);
}

/**
 * The referral still current for THIS cart in THIS browser session: the marker's
 * store when the marker names this cart, otherwise none. A cart value with no
 * matching marker is stale — a resumed cart from an earlier session.
 *
 * @param {SessionMarker|null} marker
 * @param {string|null} cartId the cart the page rendered
 * @returns {string|null}
 */
export function currentReferral(marker, cartId) {
  return marker && cartId && marker.cartId === cartId ? marker.storeNumber : null;
}

/**
 * The attribution policy.
 *
 *   nothing current + incoming           → incoming
 *   current 123 + incoming 456, 'last'   → 456
 *   current 123 + incoming 456, 'first'  → 123
 *   current 123 + nothing / malformed    → 123
 *
 * @param {{current: string|null, incoming: string|null}} input both already sanitized
 * @param {typeof REFERRAL_ATTRIBUTION_CONFIG} [config]
 * @returns {{storeNumber: string|null, captured: boolean}}
 *   `captured` — this page set or changed the referral
 */
export function resolveAttribution({ current, incoming }, config = REFERRAL_ATTRIBUTION_CONFIG) {
  if (!incoming || incoming === current) return { storeNumber: current, captured: false };
  if (current && config.attribution.model === 'first') return { storeNumber: current, captured: false };
  return { storeNumber: incoming, captured: true };
}

/**
 * The `/cart/update.js` attributes that make the cart hold `storeNumber`, or null when
 * it already does. With no referral, an existing value is cleared ('' is how the Cart
 * API removes an attribute) — that is how a stale or junk value leaves the cart.
 *
 * @param {string|null} storeNumber
 * @param {unknown} cartValue the raw attribute the cart holds now
 * @param {typeof REFERRAL_ATTRIBUTION_CONFIG} [config]
 * @returns {Record<string, string>|null}
 */
export function cartAttributesFor(storeNumber, cartValue, config = REFERRAL_ATTRIBUTION_CONFIG) {
  const have = cartValue == null ? '' : String(cartValue);
  const want = storeNumber || '';
  return have === want ? null : { [config.cartAttribute]: want };
}

/* ──────────────────────────────────────────────────────────────────────────────
   BROWSER BINDING
   ────────────────────────────────────────────────────────────────────────────── */

/** `document.cookie`, wrapped so a blocked or throwing cookie jar is just "no marker". */
function documentCookieJar(doc) {
  return {
    get(name) {
      try {
        const match = String(doc?.cookie || '').match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
        return match ? match[1] : null;
      } catch (_) {
        return null;
      }
    },
    set(name, value) {
      try {
        // No Max-Age / Expires: a session cookie, dropped when the browser quits.
        doc.cookie = `${name}=${value}; path=/; SameSite=Lax; Secure`;
      } catch (_) {
        /* cookies blocked — this page still stamps the cart */
      }
    },
    remove(name) {
      try {
        doc.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`;
      } catch (_) {
        /* nothing to remove */
      }
    },
  };
}

/**
 * Runs the page-load pass. Injectable so the tests drive it with a fake fetch and
 * cookie jar.
 *
 * @param {{
 *   win?: any,
 *   doc?: any,
 *   fetchImpl?: typeof fetch,
 *   cookies?: {get(name: string): string|null, set(name: string, value: string): void, remove(name: string): void},
 *   config?: typeof REFERRAL_ATTRIBUTION_CONFIG,
 * }} [deps]
 * @returns {{storeNumber: string|null, captured: boolean, stale: boolean, cartWrite: Promise<boolean>}|null}
 *   null when disabled
 */
export function initReferralAttribution(deps = {}) {
  const win = deps.win ?? globalThis.window;
  const doc = deps.doc ?? globalThis.document;
  const config = deps.config ?? REFERRAL_ATTRIBUTION_CONFIG;
  const doFetch = deps.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  const cookies = deps.cookies ?? documentCookieJar(doc);
  if (!config.enabled || !win) return null;

  const seed = win.__eaCartReferral && typeof win.__eaCartReferral === 'object' ? win.__eaCartReferral : {};
  const cartValue = seed.storeNumber ?? '';
  const cartCookie = cookies.get(config.shopifyCartCookie);
  const pageCartId = cartIdFromToken(cartCookie);
  const marker = parseSessionMarker(cookies.get(config.sessionCookie), config);
  const current = currentReferral(marker, pageCartId);
  const incoming = readIncomingReferral(win.location?.search, config);
  const { storeNumber, captured } = resolveAttribution({ current, incoming }, config);
  // A value on the cart that no session marker vouches for: a resumed cart from an
  // earlier browser session, or a value someone wrote by hand. It is removed below.
  const stale = !current && String(cartValue) !== '';

  /** Bind the marker to the cart that now carries the referral — or drop it. */
  const remember = (token) => {
    const cartId = cartIdFromToken(token);
    if (storeNumber && cartId) {
      const value = formatSessionMarker({ storeNumber, cartId });
      if (cookies.get(config.sessionCookie) !== value) cookies.set(config.sessionCookie, value);
    }
  };
  if (!storeNumber && marker) cookies.remove(config.sessionCookie); // a marker for another cart is finished

  win.EAReferralAttribution = Object.freeze({
    storeNumber,
    model: config.attribution.model,
    capturedOnThisPage: captured,
    // For writers that build a cart of their own (catering's Storefront cartCreate),
    // so a referral reaches those orders too. Null when there is nothing to attach.
    cartAttributes: storeNumber ? Object.freeze({ [config.cartAttribute]: storeNumber }) : null,
  });
  // Page context for ea-datalayer.js, which merges it onto every event it pushes.
  if (win.EAPageData && typeof win.EAPageData === 'object') {
    win.EAPageData.referralStore = storeNumber;
  }
  if (captured && doc?.dispatchEvent && typeof win.CustomEvent === 'function') {
    try {
      doc.dispatchEvent(
        new win.CustomEvent(REFERRAL_CAPTURED_EVENT, {
          detail: { referral_store: storeNumber, attribution_model: config.attribution.model },
        }),
      );
    } catch (_) {
      /* analytics is best-effort */
    }
  }

  // ── Cart sync ──────────────────────────────────────────────────────────────
  // A bare fetch rather than ea-availability's write chain: that chain exists to
  // order writes of the SAME keys, and nothing else writes this one. Shopify merges
  // attributes per key, so a disjoint writer cannot clobber or be clobbered
  // (tests/cart-attribute-serialization.test.mjs).
  let known = cartValue;
  let confirmed = !cartAttributesFor(storeNumber, known, config);
  if (confirmed) remember(cartCookie);
  let inFlight = null;

  /** @returns {Promise<boolean>} true when the cart now matches */
  const syncCart = () => {
    if (confirmed) return Promise.resolve(true);
    if (inFlight) return inFlight;
    const attributes = /** @type {Record<string, string>} */ (cartAttributesFor(storeNumber, known, config));
    inFlight = Promise.resolve()
      .then(() =>
        doFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        }),
      )
      .then((res) => (res && res.ok ? res.json() : null))
      .then((cart) => {
        if (!cart || !cart.attributes) return false;
        known = cart.attributes[config.cartAttribute] ?? '';
        confirmed = !cartAttributesFor(storeNumber, known, config);
        if (confirmed) remember(cart.token);
        return confirmed;
      })
      .catch((error) => {
        console.warn(`${LOG_PREFIX} cart attribute write failed; checkout is unaffected`, error);
        return false;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  // Retry path only: until this page's decision is confirmed on the cart, the next
  // cart:update (the first add-to-cart, typically) tries again. After that the
  // listener does nothing, so it can never carry a referral onto a new cart.
  try {
    doc?.addEventListener?.('cart:update', (/** @type {any} */ event) => {
      if (confirmed) return;
      try {
        const resource = event?.detail?.resource;
        const attrs = resource?.attributes;
        if (attrs && typeof attrs === 'object') known = attrs[config.cartAttribute] ?? '';
        confirmed = !cartAttributesFor(storeNumber, known, config);
        if (confirmed) remember(resource?.token);
        else syncCart();
      } catch (_) {
        /* never let attribution interfere with a cart update */
      }
    });
  } catch (_) {
    /* no document to listen on */
  }

  // With nothing to attach and nothing to clear, no request is made — and in
  // particular no empty cart is created for a visitor who arrived without a referral.
  const cartWrite = syncCart();
  return { storeNumber, captured, stale, cartWrite };
}

/** The referral store number for the cart on this page, or null. */
export function getReferralStoreNumber() {
  return globalThis.window?.EAReferralAttribution?.storeNumber ?? null;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__eaReferralAttributionInit) {
  window.__eaReferralAttributionInit = true;
  try {
    initReferralAttribution();
  } catch (error) {
    console.warn(`${LOG_PREFIX} disabled for this page`, error);
  }
}
